import { setRecoil } from 'recoil-nexus'
import { loginModalOpenState, loginSourceState } from '@/store/login'

// 单次「弹窗打开」的会话 ID：在 modal_exposure 时生成，贯穿同一次开窗内的所有 step
// 直到 modal_close。靠它能把散落在 ClickHouse 里的 step 行串成一条路径，
// 从而还原「同一次弹窗内 google 失败 → 转 email 成功」这类方式切换。
//
// 字段名保留 funnel_sid 而非 session_id：QT/aplus SDK 自带顶层 session_id（访问会话），
// 同名会在 event_kv_json 里互相覆盖，故另起名避让。（funnel_sid 描述的是「串联漏斗
// 路径的会话 ID」这一概念，与事件名 login_debug 无关，且已落表，沿用以保持数据连续。）
let debugSessionId = ''

const genDebugId = (): string => {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID()
    }
  } catch {
    // ignore，落到下面的兜底
  }
  // 兜底：时间戳 + 随机串，足够在单设备短时间内唯一。
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}

/**
 * 开启一次新的调试会话（在 modal_exposure 时调用）。
 * 之后同一次开窗内所有 trackLoginDebug 自动带上同一个 funnel_sid。
 */
export const startLoginDebugSession = (): string => {
  debugSessionId = genDebugId()
  return debugSessionId
}

/**
 * 读取当前漏斗会话 id（funnel_sid）。供后端登录事件上报（utils/loginClientReport.ts）
 * 复用作 flow_id，使前端 QT 漏斗与后端结构化日志共用同一会话 id、可直接对账。
 */
export const getLoginFlowId = (): string => debugSessionId

/**
 * 登录埋点，统一上报到 QuickTracking 的 `login_debug` 事件（logType OTHER）。
 *
 * 一个事件承载两类用途：
 * 1. 转化漏斗（用户视角）：modal_exposure → email_input_start → email_continue →
 *    email_code_sent → email_code_input_start → email_code_input_complete →
 *    submit(method=email) → success / method_fail / modal_close。
 * 2. 技术链路排查（实现视角）：firebase_signin_start / firebase_signin_resolved /
 *    firebase_id_token / token_received / popup_blocked_fallback /
 *    redirect_fallback_resolved / redirect_fallback_login_done /
 *    redirect_fallback_failed / sign_out / auth_failure，定位移动端三方登录间歇失败、
 *    登录后被踢、redirect 兜底链路等问题。
 *
 * 每条事件带 funnel_sid（单次开窗会话 ID）。注意：
 * - 弹窗内的 step（曝光/点击/submit/success/各 firebase_* 等）能正确串到同一 funnel_sid。
 * - 跨页/链路外的 step（redirect 兜底回跳后整页跳转后内存已重置、
 *   sign_out 发生在 session 结束后）funnel_sid 可能为空或失真，这类节点靠 step
 *   名 + method/status 等字段排查，不依赖 session 串联。
 *
 * 直接推 aplus_queue（不走 useQuickTracking hook）：部分调用点在模块级 / 非组件
 * 环境（auth.ts 等），用不了 hook；trackEvent 底层也只是推队列，故统一收敛到这个
 * 无依赖、SSR 安全的工具函数。
 */
export const trackLoginDebug = (
  step: string,
  extra?: Record<string, unknown>
) => {
  if (typeof window === 'undefined' || !window.aplus_queue) return
  window.aplus_queue.push({
    action: 'aplus.record',
    arguments: [
      'login_debug',
      'OTHER',
      { step, funnel_sid: debugSessionId, ...extra },
    ],
  })
}

/**
 * 打开登录弹窗并记录来源场景（漏斗的「曝光来源」维度）。
 *
 * 一期：曝光按 source=unknown 统计整体转化。
 * 二期：把各处裸 `setLoginOpen(true)` 逐步迁移为 `openLogin('chat_send')` 等，
 * 即可在漏斗里按场景拆分「哪个场景要求登录、哪个场景转化高」。
 */
export const openLogin = (source: string) => {
  setRecoil(loginSourceState, source)
  setRecoil(loginModalOpenState, true)
}
